import { getAssetsByType } from '../core/assetsService.js';
import { getUserProfile } from '../auth/userService.js';
import { isDevAdaptationEnabled } from '../../utils/devAdaptation';

// Asset Type para FeatureFlag: 11
const FEATURE_FLAGS_ASSET_TYPE = 11;

// Cache para evitar múltiplas chamadas com os mesmos parâmetros
const featureFlagsCache = new Map();

/**
 * Gera chave de cache baseada nos parâmetros
 */
const getCacheKey = (language, profile) => {
  return `${language}:${profile || 'default'}`;
};

/**
 * Processa os assets do CMS para obter os FeatureFlags e outras configurações
 * O campo 'url' do asset contém o JSON com feature flags e possivelmente outras configs
 */
const processFeatureFlagsAsset = async (assets) => {
  try {
    // Busca o primeiro asset ativo (pode haver múltiplos por idioma/perfil)
    if (assets.length > 0 && assets[0].url) {
      const response = await fetch(assets[0].url);
      const configData = await response.json();
      
      // O JSON pode conter apenas FEATURE_FLAGS ou a configuração completa
      // Se tiver FEATURE_FLAGS, retorna apenas isso
      // Se não tiver, assume que o objeto inteiro são os FeatureFlags
      if (configData.FEATURE_FLAGS) {
        return configData;
      }
      
      // Se não tem FEATURE_FLAGS, assume que o objeto inteiro são os flags
      return {
        FEATURE_FLAGS: configData,
        ...(configData.CONFIGS && { CONFIGS: configData.CONFIGS }),
        ...(configData.EXTRA_FIELDS && { EXTRA_FIELDS: configData.EXTRA_FIELDS }),
        ...(configData.SIGNUP_TYPE && { SIGNUP_TYPE: configData.SIGNUP_TYPE }),
        ...(configData.CAMPAIGN_ID && { CAMPAIGN_ID: configData.CAMPAIGN_ID }),
        ...(configData.SEGMENT_DEFAULT && { SEGMENT_DEFAULT: configData.SEGMENT_DEFAULT }),
        ...(configData.AUTHENTICATION_TYPE && { AUTHENTICATION_TYPE: configData.AUTHENTICATION_TYPE }),
        ...(configData.WALLETS && Array.isArray(configData.WALLETS) && { WALLETS: configData.WALLETS }),
        ...(configData.PROFILE_SELECT && { PROFILE_SELECT: configData.PROFILE_SELECT }),
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error processing feature flags asset:', error);
    return null;
  }
};

export const featureFlagsService = {
  /**
   * Busca FeatureFlags do CMS
   * @param {string} language - Idioma (padrão: 'pt-BR')
   * @param {string} profile - Perfil do usuário (padrão: 'gold')
   * @param {boolean} useCache - Se deve usar cache (padrão: true)
   * @returns {Promise<Object|null>} Objeto com FeatureFlags ou null
   */
  async getFeatureFlags(language = 'pt-BR', profile = getUserProfile(), useCache = true) {
    try {
      const cacheKey = getCacheKey(language, profile);
      
      // Verifica cache se habilitado
      if (useCache && featureFlagsCache.has(cacheKey)) {
        return featureFlagsCache.get(cacheKey);
      }
      
      let featureFlags;

      if (isDevAdaptationEnabled) {
        // Em desenvolvimento local: usa localhost.json em vez dos assets do servidor
        const response = await fetch('/hosts/localhost.json');
        const configData = await response.json();
        featureFlags = configData.FEATURE_FLAGS
          ? configData
          : { FEATURE_FLAGS: configData, ...configData };
      } else {
        // Busca assets do CMS usando o novo serviço unificado
        const assets = await getAssetsByType(FEATURE_FLAGS_ASSET_TYPE, {
          language,
          profile,
          requireAuth: false, // FeatureFlags podem ser públicos
        });
        featureFlags = await processFeatureFlagsAsset(assets);
      }
      
      // Armazena no cache se habilitado
      if (useCache && featureFlags) {
        featureFlagsCache.set(cacheKey, featureFlags);
      }
      
      return featureFlags;
    } catch (error) {
      console.error('Error fetching feature flags:', error);
      return null;
    }
  },
  
  /**
   * Limpa o cache de feature flags
   */
  clearCache() {
    featureFlagsCache.clear();
  },
  
  /**
   * Remove entrada específica do cache
   */
  clearCacheEntry(language, profile) {
    const cacheKey = getCacheKey(language, profile);
    featureFlagsCache.delete(cacheKey);
  }
};

